import { Box, LinearProgress, Typography } from '@mui/material'
import { type ReactNode, useEffect, useState } from 'react'

const kilo = 1000
const mega = 1000000

const getSize = (size: number) => {
  if (size > mega) {
    return `${Math.round((size / mega) * 100) / 100} MB`
  } else if (size > kilo) {
    return `${Math.round((size / kilo) * 100) / 100} KB`
  }
  return `${size} B`
}

const svgIcon = (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M11 0.75H27C27.0911 0.75 27.1793 0.777281 27.2539 0.827148L27.3232 0.883789L38.1162 11.6768C38.2019 11.7625 38.25 11.8788 38.25 12V36C38.25 37.7949 36.7949 39.25 35 39.25H11C9.20507 39.25 7.75 37.7949 7.75 36V4C7.75 2.20507 9.20508 0.75 11 0.75Z"
      fill="white"
      stroke="#D5D7DA"
      strokeWidth="1.5"
    />
    <path d="M27 0.5V8C27 10.2091 28.7909 12 31 12H38.5" stroke="#D5D7DA" strokeWidth="1.5" />
    <rect x="1" y="18" width="33" height="16" rx="2" fill="#099250" />
    <path
      d="M6.31214 22.7273L7.77876 25.206H7.83558L9.3093 22.7273H11.0458L8.82635 26.3636L11.0955 30H9.32706L7.83558 27.5178H7.77876L6.28729 30H4.52592L6.8022 26.3636L4.56854 22.7273H6.31214ZM12.0188 30V22.7273H13.5565V28.7322H16.6744V30H12.0188ZM21.638 24.8189C21.6096 24.5324 21.4876 24.3099 21.2722 24.1513C21.0568 23.9927 20.7644 23.9134 20.3951 23.9134C20.1441 23.9134 19.9322 23.9489 19.7594 24.0199C19.5866 24.0885 19.454 24.1844 19.3617 24.3075C19.2717 24.4306 19.2267 24.5703 19.2267 24.7266C19.222 24.8568 19.2492 24.9704 19.3084 25.0675C19.37 25.1645 19.454 25.2486 19.5605 25.3196C19.6671 25.3883 19.7902 25.4486 19.9299 25.5007C20.0695 25.5504 20.2187 25.593 20.3773 25.6286L21.0307 25.7848C21.348 25.8558 21.6391 25.9505 21.9043 26.0689C22.1694 26.1873 22.3991 26.3329 22.5932 26.5057C22.7873 26.6785 22.9377 26.8821 23.0442 27.1165C23.1531 27.3509 23.2087 27.6196 23.2111 27.9226C23.2087 28.3677 23.0951 28.7536 22.8702 29.0803C22.6477 29.4046 22.3257 29.6567 21.9043 29.8366C21.4853 30.0142 20.9798 30.103 20.388 30.103C19.8008 30.103 19.2895 30.013 18.8539 29.8331C18.4206 29.6532 18.0821 29.3868 17.8382 29.0341C17.5968 28.679 17.4701 28.2398 17.4583 27.7166H18.9462C18.9628 27.9605 19.0326 28.1641 19.1557 28.3274C19.2812 28.4884 19.4481 28.6103 19.6564 28.6932C19.8671 28.7737 20.1051 28.8139 20.3702 28.8139C20.6306 28.8139 20.8567 28.776 21.0485 28.7003C21.2426 28.6245 21.3929 28.5192 21.4995 28.3842C21.606 28.2493 21.6593 28.0942 21.6593 27.919C21.6593 27.7557 21.6107 27.6184 21.5137 27.5071C21.419 27.3958 21.2793 27.3011 21.0946 27.223C20.9123 27.1449 20.6886 27.0739 20.4235 27.0099L19.6316 26.8111C19.0184 26.6619 18.5343 26.4287 18.1792 26.1115C17.824 25.7943 17.6477 25.367 17.65 24.8295C17.6477 24.3892 17.7649 24.0045 18.0016 23.6754C18.2407 23.3464 18.5686 23.0895 18.9853 22.9048C19.4019 22.7202 19.8754 22.6278 20.4057 22.6278C20.9455 22.6278 21.4166 22.7202 21.8191 22.9048C22.2239 23.0895 22.5388 23.3464 22.7637 23.6754C22.9886 24.0045 23.1046 24.3857 23.1117 24.8189H21.638ZM25.6871 22.7273L27.1538 25.206H27.2106L28.6843 22.7273H30.4208L28.2013 26.3636L30.4705 30H28.7021L27.2106 27.5178H27.1538L25.6623 30H23.9009L26.1772 26.3636L23.9435 22.7273H25.6871Z"
      fill="white"
    />
  </svg>
)

export const UploadFileRow = ({ file, action }: { file: File; action: ReactNode }) => {
  const [level, setLevel] = useState(0)

  useEffect(() => {
    setLevel(100)
  }, [file])

  return (
    <Box
      display="flex"
      gap={2}
      alignItems="flex-start"
      p={2}
      borderRadius={4}
      sx={({ palette }) => ({ border: `1px solid ${palette.grey[300]}` })}
    >
      {svgIcon}
      <Box flex={1}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography>{file.name}</Typography>
            <Typography>{getSize(file.size)}</Typography>
          </Box>
          {action}
        </Box>
        <LinearProgress
          color="success"
          sx={{ height: 8, borderRadius: 16, mt: 1 }}
          variant="determinate"
          value={level}
        />
      </Box>
    </Box>
  )
}
